import { useEffect, useRef } from 'react';
import { useGSAP } from '@gsap/react';
import { gsap } from 'gsap';

import { cn } from '#/lib/utils';

interface RegulatedProps {
  shouldAnimate?: boolean;
  className?: string;
}

export function Regulated({
  shouldAnimate = false,
  className,
}: RegulatedProps) {
  const containerRef = useRef<HTMLDivElement>(null);
  const progressBarRef = useRef<HTMLDivElement>(null);
  const counterRef = useRef<HTMLSpanElement>(null);
  const timelineRef = useRef<GSAPTimeline>(null);

  useGSAP(() => {
    const mm = gsap.matchMedia();
    if (containerRef.current && progressBarRef.current) {
      // Set initial states
      gsap.set(containerRef.current, {
        y: 20,
        opacity: 0,
        scale: 0.95,
      });

      gsap.set(progressBarRef.current, {
        width: '0%',
      });

      // Create timeline but don't play it
      function createTimeline(delay: number) {
        timelineRef.current = gsap
          .timeline({ paused: true, delay })
          .to(containerRef.current, {
            y: 0,
            opacity: 1,
            scale: 1,
            duration: 0.8,
          })
          .to(
            counterRef.current,
            {
              textContent: '100',
              snap: 'textContent',
              duration: 1.2,
            },
            0.2,
          )
          .to(
            progressBarRef.current,
            {
              width: '100%',
              duration: 1.2,
            },
            0.2,
          );
      }
      mm.add('(min-width: 768px)', () => {
        createTimeline(1.5);
      });
      mm.add('(max-width: 767px)', () => {
        createTimeline(1.2);
      });
    }
  }, []);

  useEffect(() => {
    if (shouldAnimate && timelineRef.current) {
      timelineRef.current.play();
    }
  }, [shouldAnimate]);
  return (
    <div
      ref={containerRef}
      className={cn(
        'bg-white rounded-16 p-18 s:p-10 m:w-300 w-[160px] s:w-170 l:p-20  shadow-[0_9.622px_45.946px_0.729px_rgba(48,46,122,0.09)] absolute m:top-40 l:top-80 m:left-40 left-50 top-15 z-10 l:left-[30%] s:left-100 xl:left-[35%] l:w-[330px] min-[500px]:w-230 min-[500px]:top-50 min-[500px]:left-130',
        className,
      )}
    >
      <div className="text-charcoal">
        <div className="text-left">
          <div className="flex items-baseline">
            <span
              className="font-[500] text-22 s:text-30 m:text-42 leading-none"
              ref={counterRef}
            >
              0
            </span>
            <span className="font-[500] ml-1 text-11 m:text-22 leading-none">
              %
            </span>
          </div>
          <p className="text-9 m:text-18 s:text-14 l:text-22 leading-none mt-4">
            of site traffic regulated
          </p>
        </div>

        {/* Progress bar */}
        <div className="w-full bg-grayscale-01 rounded-10 m:h-10 s:h-10 h-6 my-7 m:my-14 s:my-10">
          <div
            ref={progressBarRef}
            className="bg-cipher-04 h-full rounded-full"
          />
        </div>

        {/* Time period */}
        <div className="flex items-end gap-10">
          <p className="text-7 m:text-14 s:text-12 l:text-18 ml-auto text-grayscale-04">
            Sampled over the last 7 days
          </p>
        </div>
      </div>
    </div>
  );
}
